/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { Button } from './button';
import { motion, AnimatePresence } from 'framer-motion';
import Vapi from '@vapi-ai/web';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from './tooltip';
import { SmallSlideSelector } from './small-slide-selector';
import { AudioLinesIcon } from './icons/audio-lines-icon';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import { cn } from '@/lib/utils';
import { useSnapshot } from 'valtio';
import { voiceStore, voiceActions } from '@/stores/voiceStore';

// Initialize Vapi with your API key
const vapi = new Vapi(import.meta.env.VITE_VAPI_PUBLIC_KEY);

interface VoiceControlsProps {
	onVolumeChange?: (volume: number) => void;
	onSpeakingStateChange: (isSpeaking: boolean) => void;
	onOpenChange?: (isOpen: boolean) => void;
	isMinimized?: boolean;
	isConnected?: boolean;
	setIsConnected?: (connected: boolean) => void;
	userData?: {
		id?: Id<'users'>;
		email?: string;
		name?: string;
		[key: string]: any; // Allow any additional user data
	};
	onConnectionChange?: (isConnected: boolean) => void;
	onNavigationStart?: () => Promise<void>;
	minimizeDialogRef?: React.MutableRefObject<{ minimize: () => void }>;
	onMinimize?: () => void;
	handleClose?: () => void;
}

export function VoiceControls({
	onVolumeChange,
	onSpeakingStateChange,
	onOpenChange,
	isMinimized = false,
	isConnected,
	setIsConnected,
	userData,
	onConnectionChange,
	onNavigationStart,
	minimizeDialogRef,
	onMinimize,
	handleClose,
}: VoiceControlsProps) {
	const [logs, setLogs] = React.useState<
		Array<{ time: string; message: string; type: 'info' | 'error' }>
	>([]);
	const logContainerRef = React.useRef<HTMLDivElement>(null);
	const micStreamRef = React.useRef<MediaStream | null>(null);
	const isTestingRef = React.useRef(false);
	const isConversationActiveRef = React.useRef(false);
	const isInitialGreetingRef = React.useRef(true);
	const isTransitioningRef = React.useRef(false);
	const lastMessageIdRef = React.useRef('');
	const previousVolumeRef = React.useRef(0);

	// Use Valtio store
	const state = useSnapshot(voiceStore);

	const addLog = React.useCallback(
		(message: string, type: 'info' | 'error' = 'info') => {
			const time = new Date().toLocaleTimeString();
			setLogs((prev) => {
				// Check if this message is the same as the last one
				if (prev.length > 0) {
					const lastLog = prev[prev.length - 1];
					if (lastLog.message === message && lastLog.type === type) {
						// Skip duplicate
						return prev;
					}
				}
				// Add new log if not duplicate
				const newLogs = [...prev, { time, message, type }];

				// Scroll to bottom after update
				setTimeout(() => {
					if (logContainerRef.current) {
						logContainerRef.current.scrollTop =
							logContainerRef.current.scrollHeight;
					}
				}, 0);

				return newLogs;
			});
		},
		[setLogs]
	);

	const handleMessage = React.useCallback(
		(message: any) => {
			// Handle transcripts
			if (message.type === 'transcript') {
				const messageId = `${message.type}-${message.transcriptType}-${message.transcript}`;
				if (messageId === lastMessageIdRef.current) return;
				lastMessageIdRef.current = messageId;

				if (message.transcriptType === 'final' && message.transcript) {
					addLog(
						state.isAssistantSpeaking ||
							isInitialGreetingRef.current
							? `🗣️ Assistant: ${message.transcript}`
							: `💬 You: ${message.transcript}`
					);
				}
			} else if (
				message.type === 'conversation-update' &&
				message.messages?.length > 0
			) {
				// Handle conversation updates with messages
				const lastMessage =
					message.messages[message.messages.length - 1];
				if (lastMessage.role === 'assistant' && lastMessage.content) {
					const messageId = `message-${lastMessage.id}`;
					if (messageId === lastMessageIdRef.current) return;
					lastMessageIdRef.current = messageId;
					addLog(`🤖 Assistant: ${lastMessage.content}`);
				}
			} else if (message.content) {
				const messageId = `direct-${message.id}`;
				if (messageId === lastMessageIdRef.current) return;
				lastMessageIdRef.current = messageId;
				// Ensure message.content is treated as a string for logging
				const logContent =
					typeof message.content === 'string'
						? message.content
						: JSON.stringify(message.content);
				addLog(`🤖 Assistant: ${logContent}`);
			}
		},
		[state.isAssistantSpeaking, addLog]
	);

	// Query latest content machines for each platform
	const twitterxMachine = useQuery(
		api.contentMachine.getLatestForPlatform,
		userData?.id
			? { userId: userData.id as Id<'users'>, platform: 'twitterx' }
			: 'skip'
	);
	const linkedinMachine = useQuery(
		api.contentMachine.getLatestForPlatform,
		userData?.id
			? { userId: userData.id as Id<'users'>, platform: 'linkedin' }
			: 'skip'
	);
	const facebookMachine = useQuery(
		api.contentMachine.getLatestForPlatform,
		userData?.id
			? { userId: userData.id as Id<'users'>, platform: 'facebook' }
			: 'skip'
	);
	const instagramMachine = useQuery(
		api.contentMachine.getLatestForPlatform,
		userData?.id
			? { userId: userData.id as Id<'users'>, platform: 'instagram' }
			: 'skip'
	);

	const cleanupMicStream = React.useCallback(() => {
		if (micStreamRef.current) {
			micStreamRef.current.getTracks().forEach((track) => track.stop());
			micStreamRef.current = null;
			voiceActions.setMicStream(null);
		}
	}, []);

	const handleCallStart = React.useCallback(() => {
		voiceActions.setConnecting(false);
		voiceActions.setConnected(true);
		isInitialGreetingRef.current = true;
		isConversationActiveRef.current = true;
		onConnectionChange?.(true);

		// Ensure we have a mic stream when connection starts
		if (!micStreamRef.current) {
			navigator.mediaDevices
				.getUserMedia({ audio: true })
				.then((stream) => {
					micStreamRef.current = stream;
					voiceActions.setMicStream(stream);
					// If we were muted before, apply mute state to new stream
					if (state.isMuted) {
						stream.getAudioTracks().forEach((track) => {
							track.enabled = false;
						});
					}
				})
				.catch((error) => {
					console.error('Failed to get mic stream:', error);
				});
		}
	}, [onConnectionChange, state.isMuted]);

	const handleCallEnd = React.useCallback(() => {
		if (!isMinimized && !isConversationActiveRef.current) {
			voiceActions.setConnected(false);
			onConnectionChange?.(false);
		}
	}, [isMinimized, onConnectionChange]);

	const handleSpeechStart = React.useCallback(() => {
		voiceActions.setAssistantSpeaking(true);
		onSpeakingStateChange?.(true);
	}, [onSpeakingStateChange]);

	const handleSpeechEnd = React.useCallback(() => {
		isInitialGreetingRef.current = false;
		voiceActions.setAssistantSpeaking(false);
		onSpeakingStateChange?.(false);
	}, [onSpeakingStateChange]);

	const handleVolumeLevel = React.useCallback(
		(level: number) => {
			voiceActions.setVolumeLevel(level);
			onVolumeChange?.(level);
		},
		[onVolumeChange]
	);

	const handleError = React.useCallback(
		(error: Error) => {
			console.error('Vapi error:', error);
			addLog(`❌ Error: ${error.message}`, 'error');
			voiceActions.setConnecting(false);
		},
		[addLog]
	);

	const handleMuteToggle = React.useCallback(() => {
		// console.log('🔇 [VoiceControls] Mute toggle requested:', {
		// 	hasStream: !!voiceStore.micStream,
		// 	streamTracks: voiceStore.micStream?.getAudioTracks().length,
		// 	isConnected: state.isConnected,
		// 	isTesting: state.isTesting,
		// 	currentMuteState: state.isMuted,
		// 	timestamp: new Date().toISOString(),
		// });

		// Get new mute state (opposite of current)
		const newMuteState = !state.isMuted;

		// Update Vapi mute state first
		if (state.isConnected) {
			vapi.setMuted(newMuteState);
			// console.log('🎤 [VoiceControls] Vapi mute state updated:', {
			// 	isMuted: newMuteState,
			// 	vapiMuted: vapi.isMuted(),
			// 	timestamp: new Date().toISOString(),
			// });
		}

		// Update our UI state
		voiceActions.toggleMute();

		// Apply mute state to stream if we have one
		if (voiceStore.micStream) {
			voiceStore.micStream.getAudioTracks().forEach((track) => {
				track.enabled = !newMuteState;
				console.log('🎤 Track state updated:', {
					label: track.label,
					enabled: track.enabled,
					newMuteState,
				});
			});
		}
	}, [state.isConnected, state.isMuted]);

	// Add keyboard shortcut for mute toggle
	React.useEffect(() => {
		const handleKeyPress = (event: KeyboardEvent) => {
			// Only handle spacebar if we're connected or testing and not in an input/textarea
			if (
				event.code === 'Space' &&
				(state.isConnected || state.isTesting) &&
				!(event.target instanceof HTMLInputElement) &&
				!(event.target instanceof HTMLTextAreaElement)
			) {
				event.preventDefault(); // Prevent page scroll
				handleMuteToggle();
			}
		};

		window.addEventListener('keydown', handleKeyPress);
		return () => window.removeEventListener('keydown', handleKeyPress);
	}, [handleMuteToggle, state.isConnected, state.isTesting]);

	// Set up event listeners
	React.useEffect(() => {
		// Only log on mount
		if (!isMinimized) {
			// console.log('🎙️ [VoiceControls] Ready');
		}

		// Set up event listeners
		vapi.on('message', handleMessage);
		vapi.on('call-start', handleCallStart);
		vapi.on('call-end', handleCallEnd);
		vapi.on('speech-start', handleSpeechStart);
		vapi.on('speech-end', handleSpeechEnd);
		vapi.on('volume-level', handleVolumeLevel);
		vapi.on('error', handleError);

		// Cleanup function
		return () => {
			vapi.off('message', handleMessage);
			vapi.off('call-start', handleCallStart);
			vapi.off('call-end', handleCallEnd);
			vapi.off('speech-start', handleSpeechStart);
			vapi.off('speech-end', handleSpeechEnd);
			vapi.off('volume-level', handleVolumeLevel);
			vapi.off('error', handleError);
		};
	}, [
		handleMessage,
		handleCallStart,
		handleCallEnd,
		handleSpeechStart,
		handleSpeechEnd,
		handleVolumeLevel,
		handleError,
		isMinimized,
	]);

	const startConversation = React.useCallback(
		async (event?: React.MouseEvent) => {
			event?.preventDefault();
			try {
				voiceActions.setConnecting(true);
				addLog('🎤 Starting conversation...');
				// console.log(
				// 	'🎙️ [Voice] Starting conversation with user data:',
				// 	{
				// 		hasUserData: !!userData,
				// 		userData: userData,
				// 		timestamp: new Date().toISOString(),
				// 	}
				// );

				isConversationActiveRef.current = true;
				voiceActions.setConnected(true);
				onConnectionChange?.(true);

				// Only get a new stream if we don't already have one
				if (!micStreamRef.current) {
					const stream = await navigator.mediaDevices.getUserMedia({
						audio: true,
					});
					micStreamRef.current = stream;
					addLog('🎤 Microphone access granted');
				}

				// Start microphone monitoring for visualization
				const audioContext = new AudioContext();
				const source = audioContext.createMediaStreamSource(
					micStreamRef.current
				);
				const analyzer = audioContext.createAnalyser();
				analyzer.fftSize = 512;
				analyzer.smoothingTimeConstant = 0.8;
				source.connect(analyzer);
				addLog('🔊 Audio analyzer connected');

				const dataArray = new Uint8Array(analyzer.frequencyBinCount);
				let animationFrameId: number;
				let lastLogTime = 0;

				const updateVolume = () => {
					// Only stop if we're not minimized and the conversation is not active
					if (!isConversationActiveRef.current && !isMinimized) {
						if (animationFrameId) {
							cancelAnimationFrame(animationFrameId);
						}
						micStreamRef.current
							?.getTracks()
							.forEach((track) => track.stop());
						return;
					}

					analyzer.getByteFrequencyData(dataArray);
					const average =
						dataArray.reduce((a, b) => a + b) / dataArray.length;
					const normalizedVolume = Math.min(
						Math.pow((average / 128) * 1.75, 1.2),
						1
					);

					// Only process user volume when the assistant is not speaking and not in initial greeting
					if (
						!state.isAssistantSpeaking &&
						!isInitialGreetingRef.current &&
						normalizedVolume > 0.15
					) {
						voiceActions.setVolumeLevel(normalizedVolume);
						onVolumeChange?.(normalizedVolume);

						// Only log volume during mic testing
						if (isTestingRef.current) {
							const now = Date.now();
							if (now - lastLogTime > 300) {
								addLog(
									`🎤 User volume - Level: ${Math.round(normalizedVolume * 100)}%`
								);
								lastLogTime = now;
							}
						}
					}

					animationFrameId = requestAnimationFrame(updateVolume);
				};

				addLog('🔄 Starting volume detection...');
				updateVolume();

				// Start VAPI conversation with user data if available
				// console.log('📝 [Voice] Raw user data:', {
				// 	name: userData?.name,
				// 	email: userData?.email,
				// 	timestamp: new Date().toISOString(),
				// });

				const vapiConfig = {
					// model: { // Example: Specify a model if needed
					//   provider: "openai",
					//   model: "gpt-4o",
					//   temperature: 0.7,
					// },
					// voice: { // Example: Specify a voice if needed
					//  provider: "11labs",
					//  voiceId: "burt",
					// },
					// firstMessage: "Hello! How can I help you today?", // Example
					variableValues: userData
						? {
								usersName: userData.name,
								targetEmail: userData.email,
								currentTime: new Date().toLocaleTimeString(),
								twitterxContentMachineId: twitterxMachine?._id,
								linkedinContentMachineId: linkedinMachine?._id,
								facebookContentMachineId: facebookMachine?._id,
								instagramContentMachineId:
									instagramMachine?._id,
							}
						: {
								currentTime: new Date().toISOString(),
							},
					clientMessages: undefined as any,
					serverMessages: undefined as any,
				};

				// console.log('🎯 [Voice] Prepared VAPI variables:', {
				// 	variableValues: vapiConfig.variableValues,
				// 	hasUserData: !!userData,
				// 	timestamp: new Date().toISOString(),
				// });

				// console.log('🎯 [Voice] Starting VAPI with config:', {
				// 	config: vapiConfig,
				// 	assistantId: import.meta.env.VITE_VAPI_ASSISTANT_ID,
				// 	timestamp: new Date().toISOString(),
				// });

				if (!import.meta.env.VITE_VAPI_ASSISTANT_ID) {
					throw new Error(
						'VAPI Assistant ID is not configured. Please check your environment variables.'
					);
				}

				addLog(
					`🔄 Starting VAPI ${userData ? 'with user data' : 'without user data'}`,
					'info'
				);

				await vapi.start(
					import.meta.env.VITE_VAPI_ASSISTANT_ID,
					vapiConfig
				);
			} catch (error) {
				const errorMessage =
					error instanceof Error ? error.message : String(error);
				addLog(
					`❌ Failed to start conversation: ${errorMessage}`,
					'error'
				);
				console.error('Start conversation error:', error);
				voiceActions.setConnecting(false);
				isConversationActiveRef.current = false;
				onConnectionChange?.(false);

				// Clean up microphone if there was an error
				cleanupMicStream();
			}
		},
		[
			addLog,
			onConnectionChange,
			userData,
			twitterxMachine?._id,
			linkedinMachine?._id,
			facebookMachine?._id,
			instagramMachine?._id,
			isMinimized,
			state.isAssistantSpeaking,
			onVolumeChange,
			cleanupMicStream,
		]
	);

	// Update ref when state changes
	React.useEffect(() => {
		isTestingRef.current = state.isTesting;
	}, [state.isTesting]);

	const startMicTest = React.useCallback(async () => {
		try {
			addLog('🎯 Requesting microphone access...');
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: true,
			});
			addLog('✅ Microphone access granted');

			micStreamRef.current = stream;
			voiceActions.setTesting(true);
			isTestingRef.current = true;
			voiceActions.setAssistantSpeaking(false);
			onSpeakingStateChange?.(false);
			addLog('🎤 Testing microphone...');

			const audioContext = new AudioContext();
			const source = audioContext.createMediaStreamSource(stream);
			const analyzer = audioContext.createAnalyser();
			analyzer.fftSize = 512;
			analyzer.smoothingTimeConstant = 0.8;
			source.connect(analyzer);
			addLog('🔊 Audio analyzer connected');

			const dataArray = new Uint8Array(analyzer.frequencyBinCount);
			let animationFrameId: number;
			let lastLogTime = 0;
			let frameCount = 0;

			const updateVolume = () => {
				if (!isTestingRef.current) {
					addLog('📉 Stopping volume updates');
					return;
				}

				analyzer.getByteFrequencyData(dataArray);
				frameCount++;

				// Calculate average volume with more sensitivity
				const average =
					dataArray.reduce((a, b) => a + b) / dataArray.length;

				// Reduced sensitivity and less responsive to quiet sounds
				const normalizedVolume = Math.min(
					Math.pow((average / 128) * 1.75, 1.2), // Halved amplification and increased power curve
					1
				);

				// Update states with a higher threshold
				if (normalizedVolume > 0.08) {
					// Increased threshold
					voiceActions.setVolumeLevel(normalizedVolume);
					onVolumeChange?.(normalizedVolume);

					// Log less frequently to reduce noise
					const now = Date.now();
					if (now - lastLogTime > 300) {
						addLog(
							`🎤 Volume detected - Level: ${Math.round(normalizedVolume * 100)}%`
						);
						lastLogTime = now;
					}
				}

				// Continue the animation loop
				animationFrameId = requestAnimationFrame(updateVolume);
			};

			addLog('🔄 Starting volume detection...');
			updateVolume();

			return () => {
				if (animationFrameId) {
					cancelAnimationFrame(animationFrameId);
				}
				if (audioContext.state !== 'closed') {
					audioContext.close();
				}
			};
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			addLog(`❌ Microphone test error: ${errorMessage}`, 'error');
			console.error('Microphone test error:', error);
			voiceActions.setTesting(false);
			isTestingRef.current = false;
		}
	}, [addLog, onSpeakingStateChange, onVolumeChange]);

	const stopMicTest = React.useCallback(() => {
		addLog('⏹️ Stopping microphone test...');
		if (micStreamRef.current) {
			micStreamRef.current.getTracks().forEach((track) => track.stop());
			micStreamRef.current = null;
		}
		voiceActions.setTesting(false);
		isTestingRef.current = false;
		voiceActions.setVolumeLevel(0);
		onVolumeChange?.(0);
		voiceActions.setAssistantSpeaking(false);
		onSpeakingStateChange?.(false);
		addLog('✅ Microphone test ended');
	}, [addLog, onVolumeChange, onSpeakingStateChange]);

	// Handle connection state changes from VoiceControls
	const handleConnectionChange = React.useCallback(
		(connected: boolean) => {
			// console.log('🔄 [VoiceControls] Connection state change:', {
			// 	connected,
			// 	isMinimized,
			// 	isConversationActive: isConversationActiveRef.current,
			// 	timestamp: new Date().toISOString(),
			// });

			// Always preserve connection state if conversation is active
			if (isConversationActiveRef.current) {
				voiceActions.setConnected(true);
				onConnectionChange?.(true);
				return;
			}

			// Only update state if not minimized or explicitly disconnecting
			if (!isMinimized || !connected) {
				voiceActions.setConnected(connected);
				onConnectionChange?.(connected);
				if (onSpeakingStateChange) {
					onSpeakingStateChange(connected);
				}
			}
		},
		[onConnectionChange, onSpeakingStateChange, isMinimized]
	);

	// Update parent about connection state changes
	React.useEffect(() => {
		// Always preserve connection if conversation is active
		if (isConversationActiveRef.current) {
			voiceActions.setConnected(true);
			onOpenChange?.(true);
			return;
		}

		// Don't update connection state if we're minimized
		if (isMinimized) {
			// console.log('💡 [VoiceControls] Preserving state while minimized');
			return;
		}

		// Only update if not minimized
		if (!isMinimized) {
			// console.log('🔄 [VoiceControls] Updating connection state:', {
			// 	newState: state.isConnected,
			// 	timestamp: new Date().toISOString(),
			// });
			onOpenChange?.(state.isConnected);
		}
	}, [state.isConnected, onOpenChange, isMinimized]);

	const stopConversation = React.useCallback(
		async (event?: React.MouseEvent) => {
			event?.preventDefault();
			// console.log('🛑 [VoiceControls] Stopping conversation:', {
			// 	isMinimized,
			// 	isConnected: state.isConnected,
			// 	isConversationActive: isConversationActiveRef.current,
			// 	timestamp: new Date().toISOString(),
			// });

			try {
				addLog('⏹️ Ending conversation...');
				isConversationActiveRef.current = false;
				voiceActions.setConnected(false);
				onConnectionChange?.(false);

				if (!isMinimized) {
					// console.log('🛑 [VoiceControls] Stopping VAPI:', {
					// 	timestamp: new Date().toISOString(),
					// });
					await vapi.stop();
					cleanupMicStream();
				}

				voiceActions.setVolumeLevel(0);
				onVolumeChange?.(0);
				addLog('✅ Conversation ended');
			} catch (error) {
				const errorMessage =
					error instanceof Error ? error.message : String(error);
				// console.error('❌ [VoiceControls] Error stopping conversation:', {
				// 	error: errorMessage,
				// 	timestamp: new Date().toISOString(),
				// });
				addLog(
					`❌ Error ending conversation: ${errorMessage}`,
					'error'
				);
			}
		},
		[
			onVolumeChange,
			isMinimized,
			onConnectionChange,
			cleanupMicStream,
			addLog,
		]
	);

	return (
		<motion.div
			initial={false}
			animate={{ top: state.showTools ? 333 : 444 }}
			transition={{ duration: 0.3, ease: 'easeInOut' }}
			className='absolute left-0 right-0 flex flex-col items-center gap-8 px-6 z-[9999]'
		>
			{/* Control Buttons */}
			<div className='flex flex-col items-center gap-4 relative'>
				{/* Log Panel with Animation - Positioned absolutely above buttons */}
				<AnimatePresence initial={false}>
					{state.showTranscript && (
						<motion.div
							initial={{ opacity: 0, y: -20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -20 }}
							transition={{ duration: 0.2 }}
							className='absolute bottom-full mb-4 w-[600px] flex justify-center'
						>
							<div
								ref={logContainerRef}
								className='w-full h-[200px] overflow-y-auto rounded-lg bg-black/20 backdrop-blur-sm p-4 pb-8 text-sm font-mono space-y-2'
							>
								<div className='flex flex-col gap-2'>
									{logs.map((log, i) => (
										<div
											key={i}
											className={cn(
												i === logs.length - 1 &&
													'text-yellow-600 dark:text-yellow-200 font-bold drop-shadow-[0_0_3px_rgba(253,224,71,0.7)]',
												log.type === 'error'
													? 'text-red-400'
													: 'text-gray-900/70 dark:text-white/70'
											)}
										>
											<span className='text-gray-900/40 dark:text-white/40'>
												[{log.time}]
											</span>{' '}
											{log.message}
										</div>
									))}
								</div>
								{/* Add more padding at the bottom */}
								<div className='h-8' aria-hidden='true' />
							</div>
						</motion.div>
					)}
				</AnimatePresence>

				{!state.isConnected && (
					<Button
						onClick={startConversation}
						type='button'
						disabled={
							state.isConnecting ||
							state.isConnected ||
							state.isTesting
						}
						className='bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 disabled:opacity-50 rounded-full px-6 py-10 text-2xl font-medium transition-all duration-200 text-gray-50 dark:text-white border border-gray-300/20 shadow-[0_0_80px_20px_rgba(236,72,153,0.6)] hover:shadow-[0_0_80px_20px_rgba(236,72,153,0.7)]'
					>
						<motion.span
							animate={{
								scale: state.isConnected ? 1 : [1, 1.1, 1],
							}}
							transition={{ repeat: Infinity, duration: 2 }}
							className='mr-2'
						>
							🔥
						</motion.span>
						{state.isConnecting
							? 'Connecting...'
							: 'Ask Prometheus'}
					</Button>
				)}
				{state.isConnected && (
					<Button
						onClick={stopConversation}
						disabled={!state.isConnected}
						className='bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 disabled:opacity-50 rounded-full px-6 py-10 text-2xl font-medium transition-all duration-200 text-gray-50 dark:text-white border border-gray-300/20 shadow-[0_0_80px_20px_rgba(239,68,68,0.6)] hover:shadow-[0_0_80px_20px_rgba(239,68,68,0.7)]'
					>
						<motion.span
							animate={{
								rotate: state.isConnected ? [0, 10, -10, 0] : 0,
							}}
							transition={{ repeat: Infinity, duration: 2 }}
							className='mr-2'
						>
							🔴
						</motion.span>
						End Conversation
					</Button>
				)}

				{/* Status Indicators */}
				<div className='flex items-center gap-4 text-sm'>
					{(state.isConnected ||
						state.isConnecting ||
						state.isTesting) && (
						<div className='flex items-center gap-2'>
							<motion.div
								animate={{
									scale:
										state.isConnected ||
										state.isConnecting ||
										state.isTesting
											? [1, 1.2, 1]
											: 1,
									backgroundColor: state.isConnecting
										? '#fbbf24'
										: state.isConnected || state.isTesting
											? '#10b981'
											: '#ef4444',
								}}
								transition={{
									repeat:
										state.isConnected ||
										state.isConnecting ||
										state.isTesting
											? Infinity
											: 0,
									duration: state.isConnecting ? 0.5 : 2,
								}}
								className='w-2 h-2 rounded-full'
							/>
							<span className='text-gray-900/80 dark:text-white/80'>
								{state.isConnecting
									? 'Connecting...'
									: state.isConnected
										? 'Connected'
										: state.isTesting
											? 'Testing Mic'
											: ''}
							</span>
						</div>
					)}
					{(state.isConnected || state.isTesting) && (
						<div className='flex items-center gap-2'>
							<motion.div
								animate={{
									scale: [1, 1.2, 1],
									backgroundColor: state.isAssistantSpeaking
										? '#ec4899'
										: '#3b82f6',
								}}
								transition={{
									repeat: Infinity,
									duration: 1,
								}}
								className='w-2 h-2 rounded-full'
							/>
							<span className='text-gray-900/80 dark:text-white/80 capitalize'>
								{state.isTesting
									? 'Mic Active'
									: state.isAssistantSpeaking
										? 'Speaking'
										: 'Listening'}
							</span>
						</div>
					)}
				</div>

				{/* Tools Section */}
				<div className='flex flex-col items-center gap-3'>
					{/* Show Tools Toggle and Mute Button Row */}
					<div className='flex items-center gap-2'>
						<div
							className='cursor-pointer hover:opacity-80 transition-opacity duration-200'
							onClick={() => voiceActions.toggleTools()}
						>
							<SmallSlideSelector
								value={state.showTools ? 'on' : 'off'}
								onChange={(value) => voiceActions.toggleTools()}
								options={[
									{
										value: 'off',
										icon: (
											<AudioLinesIcon isHovered={false} />
										),
										className:
											'bg-gradient-to-r from-gray-700 to-gray-800',
									},
									{
										value: 'on',
										icon: (
											<AudioLinesIcon isHovered={true} />
										),
										className:
											'bg-gradient-to-r from-purple-900 to-pink-900',
									},
								]}
								className='w-[111px] pointer-events-none'
							/>
						</div>

						{/* Mute Button - Always visible when connected or testing */}
						{(state.isConnected || state.isTesting) && (
							<Button
								onClick={handleMuteToggle}
								className={cn(
									'relative rounded-full text-sm px-3 py-1.5 transition-all duration-300',
									state.isMuted
										? 'bg-gradient-to-b from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
										: 'bg-gradient-to-b from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
								)}
							>
								<motion.div
									initial={false}
									animate={{
										scale: state.isMuted ? [1, 1.1, 1] : 1,
									}}
									transition={{
										repeat: state.isMuted ? Infinity : 0,
										duration: 2,
									}}
									className='absolute inset-0 rounded-full'
									style={{
										background: state.isMuted
											? 'radial-gradient(circle at center, rgba(239,68,68,0.3) 0%, transparent 70%)'
											: 'radial-gradient(circle at center, rgba(34,197,94,0.3) 0%, transparent 70%)',
										animation: state.isMuted
											? 'pulse 2s infinite'
											: 'none',
									}}
								/>
								<motion.span
									animate={{
										rotate: state.isMuted
											? [0, -10, 10, 0]
											: 0,
									}}
									transition={{
										repeat: state.isMuted ? Infinity : 0,
										duration: 2,
									}}
									className='mr-2'
								>
									🔇
								</motion.span>
								{state.isMuted ? 'Unmute' : 'Mute'}
							</Button>
						)}
					</div>

					{/* Test Mic Button - Only show if showTools is true */}
					{state.showTools && (
						<div className='flex items-center gap-2'>
							<Button
								onClick={
									state.isTesting ? stopMicTest : startMicTest
								}
								disabled={
									state.isConnected || state.isConnecting
								}
								className='bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 rounded-full text-sm px-3 py-1.5'
							>
								<motion.span
									animate={{
										scale: state.isTesting
											? [1, 1.1, 1]
											: 1,
									}}
									transition={{
										repeat: Infinity,
										duration: 2,
									}}
									className='mr-2'
								>
									🎤
								</motion.span>
								{state.isTesting ? 'Stop Test' : 'Test Mic'}
							</Button>
							{state.showTools && (
								<TooltipProvider delayDuration={0}>
									<Tooltip>
										<TooltipTrigger asChild>
											<div className='bg-gradient-to-r from-gray-900 to-black rounded-full w-5 h-5 cursor-help flex items-center justify-center shadow-[0_0_30px_5px_rgba(236,72,153,0.6)]'>
												<span className='text-gray-50 dark:text-white text-xs'>
													❔
												</span>
											</div>
										</TooltipTrigger>
										<TooltipContent
											className='bg-gradient-to-r from-gray-900/90 to-black/90 border-none text-gray-50 dark:text-white'
											side='right'
										>
											Test your microphone before
											<br /> starting a conversation
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							)}
						</div>
					)}

					{/* Volume Level Indicator - Only show if showTools is true */}
					{state.showTools &&
						(state.isConnected || state.isTesting) && (
							<div className='w-full max-w-[200px] h-2 bg-black/20 rounded-full overflow-hidden'>
								<motion.div
									className='h-full bg-gradient-to-r from-blue-500 to-purple-500'
									animate={{
										width: `${state.volumeLevel * 100}%`,
									}}
									transition={{ duration: 0.1 }}
								/>
							</div>
						)}

					{/* Transcript Toggle - Only show if showTools is true */}
					{state.showTools && (
						<div className='flex items-center gap-2'>
							<button
								onClick={() => voiceActions.toggleTranscript()}
								className='flex items-center gap-2 text-sm text-gray-900/70 dark:text-white/70 hover:text-gray-900 dark:hover:text-white transition-colors group'
							>
								<div className='w-5 h-5 rounded-md border border-pink-500/30 relative overflow-hidden bg-black/20 group-hover:border-pink-500/50 transition-colors'>
									<motion.div
										initial={false}
										animate={{
											y: state.showTranscript ? 0 : -20,
											opacity: state.showTranscript
												? 1
												: 0,
										}}
										transition={{ duration: 0.2 }}
										className='absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center'
									>
										<span className='text-gray-50 dark:text-white text-xs'>
											✓
										</span>
									</motion.div>
								</div>
								<span>View Transcript</span>
							</button>
						</div>
					)}
				</div>
			</div>
		</motion.div>
	);
}
